import React, { useEffect, useState } from 'react';
import './TicketsDeGasolinaDetails.scss';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from 'primereact/inputtext';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Chip } from 'primereact/chip';
import { useParams, useNavigate } from "react-router-dom";
import { getPurchaseOrderDetails } from "./integracion";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";

const TicketsDeGasolinaDetails = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [purchaseDetailsDataTable, setPurchaseDetailsDataTable] = useState([]);
    const [purchaseDetailFilter, setPurchaseDetailFilter] = useState(null);
    const [loading, setLoading] = useState(true);

    // Calcular totales
    const calcularTotales = () => {
        if (!purchaseDetailsDataTable || purchaseDetailsDataTable.length === 0) {
            return { subtotal: 0, iva: 0, total: 0, cantidad: 0 };
        }

        return purchaseDetailsDataTable.reduce((acc, item) => {
            return {
                subtotal: acc.subtotal + (parseFloat(item.subtotal) || 0),
                iva: acc.iva + (parseFloat(item.iva) || 0),
                total: acc.total + (parseFloat(item.total) || 0),
                cantidad: acc.cantidad + (parseFloat(item.cantidad) || 0)
            };
        }, { subtotal: 0, iva: 0, total: 0, cantidad: 0 });
    };

    const totales = calcularTotales();

    useEffect(() => {
        setLoading(true);
        getPurchaseOrderDetails(params.id).then(data => {
            setPurchaseDetailsDataTable(data);
            setLoading(false);
        });
    }, [params.id]);

    // Template para formato de moneda
    const monedaTemplate = (rowData, field) => {
        const valor = rowData[field];
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(valor || 0);
    };

    // Template para cantidad
    const cantidadTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className="pi pi-box text-primary"></i>
                <span className="font-semibold">{rowData.cantidad}</span>
            </div>
        );
    };

    // Header de la tabla
    const tableHeader = (
        <div className="flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="flex align-items-center gap-2">
                <i className="pi pi-list text-xl"></i>
                <span className="text-xl font-semibold">Detalle de Productos</span>
            </div>
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search"> </InputIcon>
                <InputText
                    placeholder="Buscar en la tabla..."
                    value={purchaseDetailFilter || ''}
                    onChange={(e) => setPurchaseDetailFilter(e.target.value)}
                    className="w-full md:w-20rem"
                />
            </IconField>
        </div>
    );

    return (
        <div className="TicketsDeGasolinaDetails">
            {/* Header con botón de regreso */}
            <Card className="header-card mb-3 w-full">
                <div className="flex justify-content-between align-items-center flex-wrap gap-3">
                    <Button
                        icon="pi pi-arrow-left"
                        rounded
                        text
                        severity="secondary"
                        onClick={() => navigate(-1)}
                        tooltip="Volver"
                        tooltipOptions={{ position: 'bottom' }}
                    />
                    <h1 className="page-title">
                        <i className="pi pi-file-edit"></i>
                        Detalle de Orden de Compra
                    </h1>
                    <Chip
                        label={`Orden #${params.id}`}
                        icon="pi pi-tag"
                        className="order-chip"
                    />
                </div>
            </Card>

            {/* Panel de resumen */}
            <div className="grid mb-3">
                <div className="col-12 md:col-6 lg:col-3">
                    <Panel className="summary-panel">
                        <div className="summary-content">
                            <div className="summary-icon subtotal-icon">
                                <i className="pi pi-calculator"></i>
                            </div>
                            <div className="summary-info">
                                <span className="summary-label">Subtotal</span>
                                <span className="summary-value">
                                    {new Intl.NumberFormat('es-MX', {
                                        style: 'currency',
                                        currency: 'MXN'
                                    }).format(totales.subtotal)}
                                </span>
                            </div>
                        </div>
                    </Panel>
                </div>

                <div className="col-12 md:col-6 lg:col-3">
                    <Panel className="summary-panel">
                        <div className="summary-content">
                            <div className="summary-icon iva-icon">
                                <i className="pi pi-percentage"></i>
                            </div>
                            <div className="summary-info">
                                <span className="summary-label">IVA</span>
                                <span className="summary-value">
                                    {new Intl.NumberFormat('es-MX', {
                                        style: 'currency',
                                        currency: 'MXN'
                                    }).format(totales.iva)}
                                </span>
                            </div>
                        </div>
                    </Panel>
                </div>

                <div className="col-12 md:col-6 lg:col-3">
                    <Panel className="summary-panel total-panel">
                        <div className="summary-content">
                            <div className="summary-icon total-icon">
                                <i className="pi pi-dollar"></i>
                            </div>
                            <div className="summary-info">
                                <span className="summary-label">Total</span>
                                <span className="summary-value">
                                    {new Intl.NumberFormat('es-MX', {
                                        style: 'currency',
                                        currency: 'MXN'
                                    }).format(totales.total)}
                                </span>
                            </div>
                        </div>
                    </Panel>
                </div>

                <div className="col-12 md:col-6 lg:col-3">
                    <Panel className="summary-panel">
                        <div className="summary-content">
                            <div className="summary-icon items-icon">
                                <i className="pi pi-shopping-cart"></i>
                            </div>
                            <div className="summary-info">
                                <span className="summary-label">Items Totales</span>
                                <span className="summary-value">
                                    {totales.cantidad.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </Panel>
                </div>
            </div>

            {/* Tabla de detalles */}
            <Card className="table-card">
                {purchaseDetailsDataTable.length > 0 ? (
                    <DataTable
                        value={purchaseDetailsDataTable}
                        header={tableHeader}
                        globalFilter={purchaseDetailFilter}
                        paginator
                        rows={20}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        scrollable
                        scrollHeight="600px"
                        stripedRows
                        loading={loading}
                        emptyMessage="No se encontraron registros"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                        className="custom-datatable"
                    >
                        <Column
                            field="id"
                            header="ID"
                            sortable
                            style={{ minWidth: '80px' }}
                        />
                        <Column
                            field="nombre"
                            header="Nombre del Producto"
                            sortable
                            style={{ minWidth: '200px' }}
                        />
                        <Column
                            field="precio"
                            header="Precio Unitario"
                            body={(rowData) => monedaTemplate(rowData, 'precio')}
                            sortable
                            style={{ minWidth: '130px' }}
                        />
                        <Column
                            field="cantidad"
                            header="Cantidad"
                            body={cantidadTemplate}
                            sortable
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="subtotal"
                            header="Subtotal"
                            body={(rowData) => monedaTemplate(rowData, 'subtotal')}
                            sortable
                            style={{ minWidth: '130px' }}
                        />
                        <Column
                            field="iva"
                            header="IVA"
                            body={(rowData) => monedaTemplate(rowData, 'iva')}
                            sortable
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="total"
                            header="Total"
                            body={(rowData) => monedaTemplate(rowData, 'total')}
                            sortable
                            style={{ minWidth: '130px' }}
                        />
                        <Column
                            field="fecha"
                            header="Fecha"
                            sortable
                            style={{ minWidth: '120px' }}
                        />
                    </DataTable>
                ) : (
                    !loading && (
                        <div className="empty-state">
                            <i className="pi pi-inbox empty-icon"></i>
                            <h3>No hay datos disponibles</h3>
                            <p>No se encontraron detalles para esta orden de compra</p>
                        </div>
                    )
                )}

                {purchaseDetailsDataTable.length > 0 && (
                    <>
                        <Divider />

                        {/* Resumen inferior */}
                        <div className="summary-footer">
                            <div className="grid">
                                <div className="col-12 md:col-8">
                                    <div className="footer-info">
                                        <i className="pi pi-info-circle"></i>
                                        <span>
                                            Total de productos: <strong>{purchaseDetailsDataTable.length}</strong>
                                        </span>
                                    </div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="footer-total">
                                        <span className="footer-total-label">Total de la Orden:</span>
                                        <span className="footer-total-value">
                                            {new Intl.NumberFormat('es-MX', {
                                                style: 'currency',
                                                currency: 'MXN'
                                            }).format(totales.total)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

export default TicketsDeGasolinaDetails;
